import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import FeedbackOutlinedIcon from "@mui/icons-material/FeedbackOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import { IconButton, ListItemIcon, ListItemText, Menu, MenuItem } from "@mui/material";
import { BlTooltip } from "Components/_Shared/BlTooltip";
import type { AnalysisVersionInfo } from "Infrastructure/ApiZfp/Schema";
import { FeatureFlag, useFeatureFlag } from "Infrastructure/Hooks/useFeatureFlag";
import { Radius, Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { type MouseEvent, useId, useState } from "react";
import { CloseAndDuplicateDialog } from "./Duplicate/CloseAndDuplicateDialog";
import { DuplicateConflictDialog } from "./Duplicate/DuplicateConflictDialog";
import { useDuplicateAction } from "./Duplicate/useDuplicateAction";
import { ReportIssueModal } from "./ReportIssue/ReportIssueModal";
import { AnalysisSharingModal } from "./Sharing/AnalysisSharingModal";
import type { AnalysisAccess } from "./useAnalysisAccess";

type Props = {
  readonly analysisName: string;
  readonly displayedVersion: AnalysisVersionInfo | undefined;
  readonly latestVersion: AnalysisVersionInfo | undefined;
  readonly access: AnalysisAccess;
};

export const AnalysisMoreActions = ({ analysisName, displayedVersion, latestVersion, access }: Props) => {
  const { t } = useResource();
  const isAnalysisSharingEnabled = useFeatureFlag(FeatureFlag.AnalysisSharing);
  const isDuplicateEnabled = useFeatureFlag(FeatureFlag.Duplicate);
  const menuId = useId();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [isSharingModalOpen, setIsSharingModalOpen] = useState(false);
  const [isReportIssueModalOpen, setIsReportIssueModalOpen] = useState(false);
  const duplicateAction = useDuplicateAction({ isEnabled: isDuplicateEnabled, isOwner: access.isOwner, displayedVersion, latestVersion });

  const canManageAccess = isAnalysisSharingEnabled && access.isOwner && displayedVersion !== undefined;

  const handleClose = () => setAnchorEl(null);

  return (
    <>
      <BlTooltip title={t(Resources.AnalysisDetail.MoreActions.Title)}>
        <IconButton
          aria-label={t(Resources.AnalysisDetail.MoreActions.Title)}
          aria-controls={anchorEl ? menuId : undefined}
          aria-haspopup="true"
          aria-expanded={anchorEl ? "true" : undefined}
          onClick={(event: MouseEvent<HTMLButtonElement>) => setAnchorEl(event.currentTarget)}
        >
          <MoreVertIcon fontSize="small" sx={{ color: "text.primary" }} />
        </IconButton>
      </BlTooltip>

      <Menu
        id={menuId}
        anchorEl={anchorEl}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        open={!!anchorEl}
        onClose={handleClose}
        slotProps={{
          paper: {
            elevation: 3,
            sx: { mt: 1, border: "1px solid", borderColor: "divider", borderRadius: Radius.M },
          },
        }}
      >
        {canManageAccess && (
          <MenuItem
            disabled={!access.isLatestVersion}
            onClick={() => {
              if (!access.isLatestVersion) return;
              handleClose();
              setIsSharingModalOpen(true);
            }}
            sx={{ py: Spacing.L, px: Spacing.XL, minHeight: "auto" }}
          >
            <ListItemIcon>
              <PeopleAltOutlinedIcon fontSize="small" />
            </ListItemIcon>
            <BlTooltip title={access.isLatestVersion ? "" : t(Resources.AnalysisDetail.Sharing.LatestVersionOnly)} placement="top">
              {/* a disabled MenuItem swallows pointer events, the text needs them back for the tooltip to open */}
              <ListItemText
                primary={t(Resources.AnalysisDetail.Sharing.Title)}
                slotProps={{ primary: { fontSize: 14 } }}
                sx={{ pointerEvents: "auto" }}
              />
            </BlTooltip>
          </MenuItem>
        )}
        {isDuplicateEnabled && displayedVersion !== undefined && (
          <MenuItem
            disabled={duplicateAction.isDisabled}
            onClick={() => {
              handleClose();
              duplicateAction.start();
            }}
            sx={{ py: Spacing.L, px: Spacing.XL, minHeight: "auto" }}
          >
            <ListItemIcon>
              <ContentCopyOutlinedIcon fontSize="small" />
            </ListItemIcon>
            <BlTooltip title={duplicateAction.disabledReason ?? ""} placement="top">
              {/* a disabled MenuItem swallows pointer events, the text needs them back for the tooltip to open */}
              <ListItemText
                primary={t(Resources.AnalysisDetail.Duplicate.Action)}
                slotProps={{ primary: { fontSize: 14 } }}
                sx={{ pointerEvents: "auto" }}
              />
            </BlTooltip>
          </MenuItem>
        )}
        <MenuItem
          onClick={() => {
            handleClose();
            setIsReportIssueModalOpen(true);
          }}
          sx={{ py: Spacing.L, px: Spacing.XL, minHeight: "auto" }}
        >
          <ListItemIcon>
            <FeedbackOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary={t(Resources.AnalysisDetail.ReportIssue.Title)} slotProps={{ primary: { fontSize: 14 } }} />
        </MenuItem>
      </Menu>

      {canManageAccess && (
        <AnalysisSharingModal
          open={isSharingModalOpen}
          onClose={() => setIsSharingModalOpen(false)}
          analysisName={analysisName}
          version={displayedVersion.version}
          sharedWithAgent={access.sharedWithAgent}
        />
      )}
      <ReportIssueModal open={isReportIssueModalOpen} onClose={() => setIsReportIssueModalOpen(false)} />
      <DuplicateConflictDialog conflict={duplicateAction.conflict} />
      {latestVersion && (
        <CloseAndDuplicateDialog
          sourceVersion={duplicateAction.closingSourceVersion}
          latestVersion={latestVersion}
          onClose={duplicateAction.closeClosingDialog}
        />
      )}
    </>
  );
};
